/**
 * Post-win submission form for the named leaderboard.
 *
 * Renders below the result card buttons. Anonymous by default — the player
 * can play without ever submitting. If they choose to submit, they enter a
 * nickname (1-20 chars, alphanumeric + spaces + dash + apostrophe) and pick
 * a country from the dropdown. The flag emoji previews next to the dropdown
 * as they change selection.
 *
 * On submit, fires the provided onSubmit callback with the chosen values.
 * Caller is responsible for routing the values into a /api/score-submit
 * call (already wired from result-card via the leaderboard client).
 */

import { COUNTRY_OPTIONS, countryToFlag } from '@/ui/countries';

export interface SubmitFormOpts {
  /** Called when the user clicks Submit. Caller handles the API call. */
  onSubmit: (data: { nickname: string; country: string }) => Promise<void>;
}

export interface SubmitFormHandle {
  element: HTMLElement;
  /** Disable the form (used after a successful submit so the player can't double-submit). */
  lock(): void;
  /** Show an error message inline. */
  showError(message: string): void;
}

export function createSubmitForm(opts: SubmitFormOpts): SubmitFormHandle {
  const root = document.createElement('section');
  root.className = 'submit-form';
  root.setAttribute('aria-label', 'Claim your spot on the leaderboard');

  const optionsHtml = COUNTRY_OPTIONS.map((c) => {
    if (c.code === '__sep__') return `<option disabled>${c.name}</option>`;
    const selected = c.code === 'AU' ? ' selected' : '';
    return `<option value="${c.code}"${selected}>${countryToFlag(c.code)} ${escapeHtml(c.name)}</option>`;
  }).join('');

  root.innerHTML = `
    <h3 class="submit-form__title">Claim your spot</h3>
    <p class="submit-form__lede">Optional. Anonymous by default.</p>
    <div class="submit-form__row">
      <label class="submit-form__label" for="lb-nickname">Nickname</label>
      <input
        type="text"
        id="lb-nickname"
        class="submit-form__input"
        maxlength="20"
        autocomplete="off"
        spellcheck="false"
        placeholder="e.g. SammyFromOps"
      />
    </div>
    <div class="submit-form__row">
      <label class="submit-form__label" for="lb-country">Country</label>
      <select id="lb-country" class="submit-form__select">
        ${optionsHtml}
      </select>
    </div>
    <div class="submit-form__actions">
      <button type="button" class="btn btn-primary submit-form__submit">Add me to the board</button>
    </div>
    <p class="submit-form__error" data-field="error" aria-live="polite"></p>
  `;

  const nicknameInput = root.querySelector('#lb-nickname') as HTMLInputElement;
  const countrySelect = root.querySelector('#lb-country') as HTMLSelectElement;
  const submitBtn = root.querySelector('.submit-form__submit') as HTMLButtonElement;
  const errorEl = root.querySelector('[data-field="error"]') as HTMLElement;

  let isLocked = false;

  function showError(message: string): void {
    errorEl.textContent = message;
  }

  function lock(): void {
    isLocked = true;
    nicknameInput.disabled = true;
    countrySelect.disabled = true;
    submitBtn.disabled = true;
    submitBtn.textContent = 'You are on the board';
  }

  submitBtn.addEventListener('click', async () => {
    const nickname = nicknameInput.value.trim();
    const country = countrySelect.value;
    showError('');
    if (!nickname) {
      showError('Pick a nickname first.');
      nicknameInput.focus();
      return;
    }
    if (!/^[A-Za-z0-9 '\-]+$/.test(nickname)) {
      showError('Letters, numbers, spaces, dashes, apostrophes only.');
      nicknameInput.focus();
      return;
    }
    if (!country || country === '__sep__') {
      showError('Pick a country.');
      countrySelect.focus();
      return;
    }
    submitBtn.disabled = true;
    submitBtn.textContent = 'Adding…';
    try {
      await opts.onSubmit({ nickname, country });
    } catch (err) {
      showError('Could not submit. Try again.');
    } finally {
      // Reset button state unless lock() was called (success path).
      // Without this, the button stays "Adding…" when the caller shows
      // an inline error and returns successfully (e.g. token_replay).
      if (!isLocked) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Add me to the board';
      }
    }
  });

  return { element: root, lock, showError };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
